import { supabase } from '../api/supabase-client';
import { projectId } from '../../utils/supabase/info';

/**
 * Вход на собственный бэкенд (`/api/*`).
 *
 * Бэкенд долго жил без проверки входа: ручки публикации, очереди отложенных,
 * отчётов отвечали любому, кто знал адрес. Теперь он требует сессию
 * пользователя — тот же JWT Supabase, что уходит в edge-функцию.
 *
 * Токен добавляется перехватом `fetch`, а не в каждом вызове: вызовов к `/api`
 * два десятка, и любой забытый сломался бы молча — 401 на одной кнопке.
 * Перехват трогает только запросы этого же сайта к `/api/` и только если
 * заголовка Authorization ещё нет: презентация и разбор комментариев уже
 * ставят его сами.
 */

const REFRESH_MARGIN_S = 60;

/** Токен текущей сессии (освежённый, если вот-вот истечёт) или null. */
export async function backendToken(): Promise<string | null> {
  let {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) return null;
  const nowSec = Math.floor(Date.now() / 1000);
  if ((session.expires_at ?? 0) - nowSec < REFRESH_MARGIN_S) {
    const { data, error } = await supabase.auth.refreshSession();
    if (!error && data.session) session = data.session;
  }
  return session.access_token;
}

/**
 * Токен без ожидания — для XHR, где заголовок ставится между open и send.
 * Сессию supabase-js держит в localStorage под этим ключом и сам освежает
 * её в фоне, так что сохранённый токен — актуальный.
 */
export function backendTokenSync(): string | null {
  try {
    const raw = window.localStorage.getItem(`sb-${projectId}-auth-token`);
    const token = raw ? (JSON.parse(raw) as { access_token?: unknown }).access_token : null;
    return typeof token === 'string' ? token : null;
  } catch {
    return null;
  }
}

/** Запрос этого сайта к бэкенду? Внешние адреса токен не получают никогда. */
export function isBackendUrl(url: string, origin = window.location.origin): boolean {
  try {
    const u = new URL(url, origin);
    return u.origin === origin && u.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

let installed = false;

export function installBackendAuth(getToken: () => Promise<string | null> = backendToken): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const original = globalThis.fetch.bind(globalThis);

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    if (!isBackendUrl(url)) return original(input, init);

    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    if (!headers.has('authorization')) {
      const token = await getToken().catch(() => null);
      if (token) headers.set('authorization', `Bearer ${token}`);
    }
    return original(input, { ...init, headers });
  };
}

/** Только для тестов: вернуть возможность установить перехват заново. */
export function resetBackendAuthForTests(): void {
  installed = false;
}
